import { useEffect, useState } from 'react';
import { NSchema as n, type NostrEvent, type NostrMetadata } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

export interface ProfileSearchResult {
  pubkey: string;
  metadata: NostrMetadata;
  event: NostrEvent;
}

/** Relationship of a search result to the current user. */
export type ProfileRelation = 'self' | 'following' | 'none';

export interface RankedProfileResult extends ProfileSearchResult {
  relation: ProfileRelation;
}

/**
 * Rank profile results the way Ditto does: the current user first, then people
 * they follow, then everyone else. Ordering within each group is preserved
 * (the relay already returns them by `sort:top`).
 */
export function rankProfiles(
  results: ProfileSearchResult[],
  userPubkey: string | undefined,
  follows: Set<string>,
): RankedProfileResult[] {
  const ranked = results.map((r): RankedProfileResult => ({
    ...r,
    relation:
      r.pubkey === userPubkey ? 'self' : follows.has(r.pubkey) ? 'following' : 'none',
  }));

  const weight = (rel: ProfileRelation) =>
    rel === 'self' ? 0 : rel === 'following' ? 1 : 2;

  return ranked
    .map((r, i) => ({ r, i }))
    .sort((a, b) => weight(a.r.relation) - weight(b.r.relation) || a.i - b.i)
    .map(({ r }) => r);
}

/** Debounce a rapidly-changing value. */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Search for Nostr profiles by name using NIP-50 full-text search, following
 * Ditto's convention: `{ kinds: [0], search: "<q> autocomplete:true sort:top" }`.
 * Requires a NIP-50 capable relay (e.g. wss://relay.ditto.pub) in the pool.
 */
export function useSearchProfiles(query: string) {
  const { nostr } = useNostr();
  const debounced = useDebounce(query.trim(), 300);

  return useQuery<ProfileSearchResult[]>({
    queryKey: ['search-profiles', debounced],
    queryFn: async (c) => {
      if (!debounced) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [0], search: `${debounced} autocomplete:true sort:top`, limit: 10 }],
        { signal },
      );

      // Parse metadata and dedupe to the newest event per pubkey.
      const byPubkey = new Map<string, ProfileSearchResult>();
      for (const event of events) {
        try {
          const metadata = n.json().pipe(n.metadata()).parse(event.content);
          const existing = byPubkey.get(event.pubkey);
          if (!existing || event.created_at > existing.event.created_at) {
            byPubkey.set(event.pubkey, { pubkey: event.pubkey, metadata, event });
          }
        } catch {
          // Skip profiles with invalid metadata JSON.
        }
      }

      return Array.from(byPubkey.values());
    },
    enabled: debounced.length >= 1,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}
